import { Card, Grid, Icon, IconButton, InputLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { get, post, postNoResponse } from "utils/WebService";
import CasosCreateEdit from "./CasosCreateEdit";
import MDSnackbar from "components/MDSnackbar";
import { ESCURO } from "assets/coresTema";

export default function Casos() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: []
    });

    const [filters, setFilters] = useState({
        pessoa: null,
        nomeCaso: "",
    });
    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(false);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [clientList, setClientList] = useState([]);
    const [recordsTotal, setRecordsTotal] = useState(0);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);
    
    const [sbMessage, setSbMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    useEffect(() => {
        get("/pessoa/list-all").then(({ list }) => setClientList(list || [])).catch(() => {});
        fetchData(tableState.current);
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const payload = {
            page: pageIndex,
            pageSize,
            sorted: sorting,
            filtered: filtered || []
        };

        post("/processo/list-casos", payload).then(({ data, pages, recordsTotal }) => {
            setData(data || []);
            setPages(pages || 0);
            setRecordsTotal(recordsTotal || 0);
            setLoadingTable(false);
        }).catch(() => setLoadingTable(false));
    }, []);

    const handleApplyFilter = () => {
        const filtered = [];
        if (filters.pessoa) filtered.push({ id: "pessoaId", value: filters.pessoa.id });
        if (filters.nomeCaso) filtered.push({ id: "nomeCaso", value: filters.nomeCaso });
        
        tableState.current.filtered = filtered;
        fetchData(tableState.current);
    };

    const handleCleanFilter = () => {
        setFilters({ pessoa: null, nomeCaso: "" });
        tableState.current.filtered = [];
        fetchData(tableState.current);
    };

    const confirmDelete = (id) => {
        setSelectedIdToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDelete = () => {
        if (!selectedIdToDelete) return;
        setDeleteDialogOpen(false);
        setLoadingTable(true);
        postNoResponse("/processo/delete-caso", { id: selectedIdToDelete })
            .then(() => {
                showSnackbar("Caso excluído com sucesso!", true);
                fetchData(tableState.current);
            })
            .catch(err => {
                showSnackbar(err.message || "Erro ao excluir caso.", false);
                setLoadingTable(false);
            })
            .finally(() => setSelectedIdToDelete(null));
    };

    const columns = [
        { header: "Cliente", accessorKey: "pessoa.nomeComum", cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell> },
        { header: "Nome do Caso", accessorKey: "nomeCaso", cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell> },
        { header: "Documento", accessorKey: "documento", cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell> },
        {
            header: "Ações",
            isSorted: false,
            cell: info => (
                <MDBox display="flex">
                    <IconButton onClick={() => { setSelectedId(info.row.original.id); setIsCreateEdit(true); }}>
                        <Icon color="info">edit</Icon>
                    </IconButton>
                    <IconButton onClick={() => confirmDelete(info.row.original.id)}>
                        <Icon color="error">delete</Icon>
                    </IconButton>
                </MDBox>
            )
        }
    ];

    if (isCreateEdit) {
        return (
            <CasosCreateEdit 
                id={selectedId} 
                close={() => { setIsCreateEdit(false); setSelectedId(null); fetchData(tableState.current); }} 
            />
        );
    }

    return (
        <MDBox pt={2} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card
                            variant="outlined"
                            sx={{
                                px: 2,
                                py: 1.5,
                                mb: 2,
                                background: darkMode ? ESCURO.cartao : "#fafafa",
                                borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                                    <MDSelect
                                        options={clientList}
                                        getOptionLabel={o => o.nomeComum}
                                        getOptionValue={o => o.id}
                                        value={filters.pessoa}
                                        onChange={v => setFilters({ ...filters, pessoa: v })}
                                        placeholder="Selecione o cliente"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome do Caso</InputLabel>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        value={filters.nomeCaso}
                                        placeholder="Informe o nome"
                                        onChange={e => setFilters({ ...filters, nomeCaso: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleApplyFilter();
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} display="flex" justifyContent="flex-end" alignItems="center">
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        onClick={() => setIsCreateEdit(true)}
                                    >
                                        Criar Caso
                                    </MDButton>
                                </Grid>

                                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                                    <MDButton size="medium"
                                        variant="contained"
                                        color="info"
                                        sx={{ mr: 2 }}
                                        onClick={handleCleanFilter}
                                    >
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton size="medium" 
                                        variant="contained" 
                                        color="info" 
                                        onClick={handleApplyFilter}
                                    >
                                        Aplicar filtro
                                    </MDButton>
                                </Grid>
                            </Grid>
                        </Card>

                        <Card>
                            <DataTable
                                tableState={tableState}
                                columnData={columns}
                                data={data}
                                totalPages={pages}
                                totalRecords={recordsTotal}
                                loading={loadingTable}
                                fetchData={fetchData}
                            />
                        </Card>
                    </Grid>
                </Grid>

                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Excluir Caso</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Tem certeza que deseja excluir este Caso? Esta ação não pode ser desfeita e pode afetar processos e andamentos vinculados.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary">Cancelar</MDButton>
                        <MDButton onClick={handleDelete} color="error">Excluir</MDButton>
                    </DialogActions>
                </Dialog>

                <MDSnackbar
                    color="success"
                    icon="check"
                    title="Sucesso"
                    content={sbMessage}
                    dateTime=""
                    open={successSB}
                    close={() => setSuccessSB(false)}
                    bgWhite
                />
                <MDSnackbar
                    color="error"
                    icon="warning"
                    title="Erro"
                    content={sbMessage}
                    dateTime=""
                    open={errorSB}
                    close={() => setErrorSB(false)}
                    bgWhite
                />
            </MDBox>
    );
}

